/**
 * Sélecteur de responsables basé sur les tags sélectionnés.
 *
 * Responsabilités :
 * - récupérer la liste des utilisateurs disponibles pour le client courant
 *   et les tags sélectionnés
 * - transformer les utilisateurs en options de select
 * - empêcher la sélection multiple d'un même responsable
 * - exposer la sélection au parent via setSelectedResponsables
 */
import { skipToken } from '@reduxjs/toolkit/query'
import { FormSelect } from 'core/components/common/FormSelect'
import { UserContext } from 'core/contexts/UserContext'
import { IUtilisateur } from 'core/interfaces/Utilisateur'
import { useGetUtilisateursByClientQuery } from 'lauria/api/utilisateurs'
import React, { ChangeEvent, useContext, useMemo, useState } from 'react'
import { FieldError } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

type SelectedTag = {
  id: number
  label: string
}

export type SelectedResponsable = {
  id: number
  label: string
}

type ResponsableOption = {
  value: string
  label: string
}

type ResponsableSelectProps = {
  selectedTags: SelectedTag[]
  selectedResponsables: SelectedResponsable[]
  setSelectedResponsables: React.Dispatch<
    React.SetStateAction<SelectedResponsable[]>
  >
  displayDefaultOption?: boolean
  width?: string
  error?: FieldError
}

export const ResponsableSelect: React.FC<ResponsableSelectProps> = ({
  selectedTags,
  selectedResponsables,
  setSelectedResponsables,
  displayDefaultOption = true,
  width = '[300px]',
  error,
}) => {
  const { t } = useTranslation()
  const { user: currentUser } = useContext(UserContext)
  const [value, setValue] = useState('')

  const idClient = currentUser?.idClient ?? null
  const currentUserId = currentUser?.idUtilisateur ?? null
  const tagIds = useMemo(
    () => selectedTags.map((tag) => tag.id),
    [selectedTags]
  )

  const queryArg =
    idClient !== null && tagIds.length > 0
      ? { idClient, params: { tagIds, itemsPerPage: 100 } }
      : skipToken

  const { data, isLoading } = useGetUtilisateursByClientQuery(queryArg)
  const utilisateurs = data?.member ?? []

  const getResponsableLabel = (utilisateur: IUtilisateur): string => {
    const prenom = utilisateur.prenom ?? ''
    const nom = utilisateur.nom ?? ''

    return `${prenom} ${nom}`.trim()
  }

  const responsableOptions = useMemo<ResponsableOption[]>(() => {
    return utilisateurs
      .filter(
        (utilisateur) =>
          utilisateur.idUtilisateur !== currentUserId &&
          !selectedResponsables.some(
            (selectedResponsable) =>
              selectedResponsable.id === utilisateur.idUtilisateur
          )
      )
      .map((utilisateur) => ({
        value: utilisateur.idUtilisateur.toString(),
        label: getResponsableLabel(utilisateur),
      }))
  }, [utilisateurs, currentUserId, selectedResponsables])

  const handleResponsableSelect = (event: ChangeEvent<HTMLSelectElement>) => {
    const selectedValue = event.target.value

    if (selectedValue === '') {
      return
    }

    const responsableId = Number(selectedValue)
    const selectedOption = responsableOptions.find(
      (option) => option.value === selectedValue
    )

    if (!selectedOption) {
      return
    }

    setSelectedResponsables((prev) => {
      const alreadySelected = prev.some(
        (responsable) => responsable.id === responsableId
      )

      if (alreadySelected) {
        return prev
      }

      return [
        ...prev,
        {
          id: responsableId,
          label: selectedOption.label,
        },
      ]
    })

    // On remet le select à son placeholder
    setValue('')
  }

  return (
    <div className="md:flex md:flex-col">
      <FormSelect
        placeholder={t('applications.form.parameters.responsablesPlaceholder')}
        options={responsableOptions}
        fieldName="responsables"
        wrapperClassName={`md:w-${width}`}
        wrapperErrorClassName="h-[0px]"
        onChange={handleResponsableSelect}
        isLoading={isLoading}
        value={value}
        displayDefaultOption={displayDefaultOption}
        error={error}
      />
    </div>
  )
}
